import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PButtonPure, PInputSearch } from "@porsche-design-system/components-react"
import type { InputTextChangeEventDetail } from "@porsche-design-system/components-react"
import { breakpoint } from "@porsche-design-system/components-react/emotion"
import type { InventoryListFilterValue, InventoryListFilterView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useMemo, useRef } from "react"
import type { SubmitEvent } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { modelStockNoVinSearchPattern } from "src/common/modelStockNoVinSearchPattern/modelStockNoVinSearchPattern"
import { ActiveFilters, Input, Tooltip } from "src/components"
import { InventoryActiveFiltersTagLists } from "./InventoryActiveFiltersTagLists"
import { filterMessages } from "./filter.messages"
import { inventoryActiveFiltersMessages } from "./inventoryActiveFilters.messages"
import { useTypingPlaceholder } from "./useTypingPlaceholder"

export interface InventoryActiveFiltersProps {
	filter: InventoryListFilterView | undefined
	isExpanded: boolean
	newCarOrderIdSearchEnabled?: boolean
	onChange: (
		value: InventoryListFilterValue,
		key: keyof InventoryListFilterValue,
		action?: "tag-dismissed" | "search",
		newValue?: string,
	) => void
	onToggleFilters: () => void
	scrollable?: boolean
	smartSearch?: {
		loading: boolean
		onChange: (event: CustomEvent<InputTextChangeEventDetail>) => void
		onSubmit: (event: SubmitEvent<HTMLFormElement>) => void
		value: string | undefined
	}
	value: InventoryListFilterValue
}

export const InventoryActiveFilters = ({
	filter,
	isExpanded,
	newCarOrderIdSearchEnabled,
	onChange,
	onToggleFilters,
	scrollable,
	smartSearch,
	value,
}: InventoryActiveFiltersProps) => {
	const { formatMessage } = useIntl()
	const { distance } = useDealerContext()

	const smartSearchPlaceholders = useMemo(
		() => [
			formatMessage(inventoryActiveFiltersMessages.smartSearchPlaceholder),
			formatMessage(inventoryActiveFiltersMessages.smartSearchPlaceholder2),
			formatMessage(inventoryActiveFiltersMessages.smartSearchPlaceholder3, { distance }),
			formatMessage(inventoryActiveFiltersMessages.smartSearchPlaceholder4),
		],
		[formatMessage, distance],
	)
	const smartSearchRef = useRef<HTMLInputElement>(null)
	useTypingPlaceholder(smartSearchRef, smartSearchPlaceholders)

	const onSearchChange = useCallback(
		(newVal: string | undefined) => onChange({ ...value, search: newVal ?? "" }, "search", "search", newVal ?? ""),
		[value, onChange],
	)

	return (
		<ActiveFilters
			onToggleFilters={onToggleFilters}
			scrollable={scrollable}
			search={
				smartSearch ? (
					<Tooltip appendToBody content={formatMessage(inventoryActiveFiltersMessages.smartSearchTooltip)}>
						<form onSubmit={smartSearch.onSubmit}>
							<SmartSearchInput
								ref={smartSearchRef}
								clear
								indicator
								onInput={smartSearch.onChange}
								value={smartSearch.value}
								label={formatMessage(inventoryActiveFiltersMessages.smartSearchButton)}
								name="smart-search"
							>
								<PButtonPure
									slot="end"
									icon="ai-spark"
									loading={smartSearch.loading}
									className="p-(--ref-p-input-slotted-padding) m-(--ref-p-input-slotted-margin)"
									aria={{ "aria-label": "Locate" }}
								/>
							</SmartSearchInput>
						</form>
					</Tooltip>
				) : (
					<StyledInput
						name="search"
						type="search"
						label={formatMessage(filterMessages.search)}
						value={value?.search}
						placeholder={formatMessage(
							newCarOrderIdSearchEnabled
								? filterMessages.searchPlaceholderExtended
								: filterMessages.searchPlaceholder,
						)}
						pattern={modelStockNoVinSearchPattern}
						debounced={500}
						onChange={onSearchChange}
					/>
				)
			}
			tagList={
				filter ? <InventoryActiveFiltersTagLists filter={filter} value={value} onChange={onChange} /> : null
			}
			toggleFiltersLabel={
				<FormattedMessage
					{...(isExpanded
						? inventoryActiveFiltersMessages.closeFilters
						: inventoryActiveFiltersMessages.showFilters)}
				/>
			}
			value={value}
			withResetButtonColumn
		/>
	)
}

const StyledInput = styled(Input)`
	min-width: 200px;
	@media (min-width: ${breakpoint.s}px) {
		width: 340px; /* roughly match longest placeholder wording */
	}
`

const SmartSearchInput = styled(PInputSearch)`
	min-width: 200px;
	@media (min-width: ${breakpoint.s}px) {
		width: 420px; /* roughly match longest placeholder wording */
	}
`
